import React from 'react'
import CircularProgress from '@material-ui/core/CircularProgress'
import { AdCreative, PacketStrategy, PacketStrategyGroup } from '@marketing-milk/interfaces'
import { radius, SurfacePalette, type } from '../../tokens'
import { CreativeCard, CreativeCardEditing } from './CreativeCard'
import { joinStrategy, StrategyGroupView } from './joinStrategy'

export interface CreativeGroupEditing extends CreativeCardEditing {
  onSaveGroup: (groupID: number, changes: { label?: string; blurb?: string | null }) => void
  onRegenerate: () => void
  regenerating: boolean
}

interface Props {
  palette: SurfacePalette
  creatives?: AdCreative[]
  /** Absent (or group-less) strategy collapses to one flat Ungrouped bucket. */
  strategy?: PacketStrategy
  loading?: boolean
  /** True when the packet has no bound sprint to load creatives from. */
  noSprint?: boolean
  /**
   * When provided, clicking a card's media opens that creative in its source editor.
   */
  onOpenSource?: (creative: AdCreative) => void
  /** Present when this is Edit mode. Present mode passes nothing. */
  editing?: CreativeGroupEditing
  /**
   * Replaces the tile. The client review surface swaps in its own card, which
   * carries approve/reject instead of comment anchors and edit affordances.
   * The list still owns each card's key.
   */
  renderCard?: (creative: AdCreative) => React.ReactNode
}

const Muted = ({ palette, children }: { palette: SurfacePalette; children: React.ReactNode }) => (
  <p style={{ margin: 0, fontSize: type.size.body, color: palette.textMuted }}>{children}</p>
)

const Grid = ({ children }: { children: React.ReactNode }) => (
  <div
    style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
      gap: 20,
    }}
  >
    {children}
  </div>
)

/**
 * Shared creative layout used by both the edit (`CreativeSection`) and Present
 * (`PresentCreativeSection`) hosts. Ads are laid out BY STRATEGIC THEME rather
 * than as one flat grid; a packet with no strategy data collapses to a single
 * Ungrouped bucket, which is exactly the old flat grid, so degradation falls
 * out of the structure instead of needing a second code path.
 *
 * Each card is its own `creative` comment anchor keyed by the bare creative id
 * (matching CreativeWizard so the thread is shared) and reveals a per-card
 * comment trigger on hover. The shared click delegation in `useCommentAnchors`
 * resolves a card trigger to the nearest anchor ancestor — the card — leaving
 * the section-chrome `section` anchor (further up) untouched.
 */
export const CreativeCardList = ({
  palette,
  creatives,
  strategy,
  loading,
  noSprint,
  onOpenSource,
  editing,
  renderCard,
}: Props) => {
  if (noSprint) return <Muted palette={palette}>This packet isn’t linked to a sprint.</Muted>
  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: palette.textMuted }}>
        <CircularProgress size={18} style={{ color: palette.accent }} />
        <span style={{ fontSize: type.size.body }}>Loading creatives…</span>
      </div>
    )
  }
  if (!creatives || creatives.length === 0) {
    return <Muted palette={palette}>No creatives in this sprint yet.</Muted>
  }

  const { groups, ungrouped } = joinStrategy(strategy, creatives)

  const renderCards = (cards: AdCreative[], pinned?: Set<number>) => (
    <Grid>
      {cards.map(creative =>
        renderCard ? (
          <React.Fragment key={creative.id}>{renderCard(creative)}</React.Fragment>
        ) : (
          <CreativeCard
            key={creative.id}
            palette={palette}
            creative={creative}
            pinned={pinned?.has(creative.id)}
            onOpenSource={onOpenSource}
            editing={editing}
          />
        )
      )}
    </Grid>
  )

  const renderUngrouped = () => (
    <UngroupedBucket
      palette={palette}
      count={ungrouped.length}
      hasGroups={groups.length > 0}
      status={strategy?.status}
      error={strategy?.error}
      editing={editing}
    >
      {renderCards(ungrouped)}
    </UngroupedBucket>
  )

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>
      {groups.map(view => (
        <GroupBlock key={view.group.id} palette={palette} view={view} editing={editing}>
          {renderCards(view.creatives, view.pinned)}
        </GroupBlock>
      ))}
      {/* Ungrouped renders last and only when it has something in it — except
        when there are no groups at all, where it IS the section. */}
      {(ungrouped.length > 0 ||
        groups.length === 0 ||
        (!!editing && (strategy?.status === 'generating' || strategy?.status === 'failed'))) &&
        renderUngrouped()}
    </div>
  )
}

const GroupBlurb = ({ palette, group }: { palette: SurfacePalette; group: PacketStrategyGroup }) =>
  group.blurb ? (
    <p style={{ margin: 0, fontSize: type.size.body, color: palette.textMuted }}>{group.blurb}</p>
  ) : null

/**
 * A theme's header plus its cards. The header renders inline here rather than
 * as its own component — nothing else needs it yet.
 *
 * Editing a label or blurb LOCKS the group (the server forces it), so
 * regeneration may still re-shuffle which ads land here but will never rewrite
 * the AE's words.
 */
const GroupBlock = ({
  palette,
  view,
  editing,
  children,
}: {
  palette: SurfacePalette
  view: StrategyGroupView
  editing?: CreativeGroupEditing
  children: React.ReactNode
}) => {
  const { group } = view

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <header style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {editing ? (
            <EditableText
              palette={palette}
              value={group.label}
              ariaLabel={`Theme name for ${group.label}`}
              onSave={label => editing.onSaveGroup(group.id, { label })}
              style={{
                fontFamily: type.family.sans,
                fontSize: type.size.body,
                fontWeight: type.weight.semibold,
                color: palette.text,
              }}
            />
          ) : (
            <h3
              style={{
                margin: 0,
                fontFamily: type.family.sans,
                fontSize: type.size.body,
                fontWeight: type.weight.semibold,
                color: palette.text,
              }}
            >
              {group.label}
            </h3>
          )}
          <span style={{ fontSize: type.size.eyebrow, color: palette.textMuted }}>
            {view.creatives.length} {view.creatives.length === 1 ? 'ad' : 'ads'}
          </span>
          {editing && group.locked && (
            <span
              title="You've edited this theme, so regeneration won't rewrite it"
              style={{ fontSize: type.size.eyebrow, color: palette.textMuted }}
            >
              <i className="fa fa-lock" /> Edited
            </span>
          )}
        </div>
        {editing ? (
          <EditableText
            palette={palette}
            value={group.blurb ?? ''}
            multiline
            placeholder="One line on why these ads belong together…"
            ariaLabel={`Description for ${group.label}`}
            onSave={blurb => editing.onSaveGroup(group.id, { blurb: blurb || null })}
            style={{ fontSize: type.size.body, color: palette.textMuted }}
          />
        ) : (
          <GroupBlurb palette={palette} group={group} />
        )}
      </header>
      {children}
    </section>
  )
}

/**
 * Ads with no membership row. This means "arrived since the last generation",
 * which is why Regenerate lives HERE and nowhere else: it is the only place the
 * AE ever needs it, and it's the one place a stale packet announces itself.
 *
 * There is deliberately no first-draft button — generation is automatic at
 * import, so an AE prepping in a hurry never faces an empty section.
 */
const UngroupedBucket = ({
  palette,
  count,
  hasGroups,
  status,
  error,
  editing,
  children,
}: {
  palette: SurfacePalette
  count: number
  hasGroups: boolean
  status?: PacketStrategy['status']
  error?: string | null
  editing?: CreativeGroupEditing
  children: React.ReactNode
}) => {
  const showStatus = !!editing && (status === 'generating' || status === 'failed')

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {(hasGroups || showStatus) && (
        <header style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <h3
            style={{
              margin: 0,
              fontFamily: type.family.sans,
              fontSize: type.size.body,
              fontWeight: type.weight.semibold,
              color: palette.text,
            }}
          >
            {!editing
              ? 'More ads'
              : status === 'failed'
              ? 'Creative strategy needs attention'
              : status === 'generating'
              ? hasGroups
                ? 'Updating creative strategy'
                : 'Building creative strategy'
              : 'Not yet grouped'}
          </h3>
          {editing && (
            <span
              title={status === 'failed' ? error ?? undefined : undefined}
              style={{ fontSize: type.size.eyebrow, color: palette.textMuted }}
            >
              {status === 'failed'
                ? 'Strategy generation failed. Try again.'
                : status === 'generating'
                ? `Grouping ${count} ${count === 1 ? 'ad' : 'ads'} into themes…`
                : `${count} new ${count === 1 ? 'ad' : 'ads'} since this was generated`}
            </span>
          )}
          {editing && (
            <button
              type="button"
              onClick={editing.onRegenerate}
              disabled={editing.regenerating}
              style={{
                border: 'none',
                background: 'transparent',
                color: palette.textMuted,
                fontSize: 12,
                cursor: editing.regenerating ? 'default' : 'pointer',
                opacity: editing.regenerating ? 0.6 : 1,
                padding: 0,
              }}
            >
              <i className={`fa fa-${editing.regenerating ? 'spinner fa-spin' : 'refresh'} mr-2`} />
              {editing.regenerating ? 'Regrouping…' : 'Regenerate'}
            </button>
          )}
        </header>
      )}
      {children}
    </section>
  )
}

/**
 * Save-on-blur text, matching `useNarrativeBlockSave`'s behaviour. Local to
 * this file for now — the justification editor lives on the card and does the
 * same thing, but two usages isn't yet a shared component.
 */
const EditableText = ({
  palette,
  value,
  onSave,
  ariaLabel,
  placeholder,
  multiline,
  style,
}: {
  palette: SurfacePalette
  value: string
  onSave: (value: string) => void
  ariaLabel: string
  placeholder?: string
  multiline?: boolean
  style?: React.CSSProperties
}) => {
  const [text, setText] = React.useState(value)
  const lastSavedRef = React.useRef(value)

  React.useEffect(() => {
    if (value !== lastSavedRef.current && text === lastSavedRef.current) setText(value)
    lastSavedRef.current = value
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  const shared: React.CSSProperties = {
    ...style,
    width: '100%',
    background: 'transparent',
    border: '1px solid transparent',
    borderRadius: radius.sm,
    padding: 4,
    fontFamily: type.family.sans,
  }

  const onBlur = () => {
    const next = text.trim()
    if (next === lastSavedRef.current) return
    lastSavedRef.current = next
    onSave(next)
  }

  return multiline ? (
    <textarea
      value={text}
      aria-label={ariaLabel}
      placeholder={placeholder}
      maxLength={400}
      onChange={e => setText(e.target.value)}
      onBlur={onBlur}
      style={{ ...shared, minHeight: 40, resize: 'vertical', borderColor: palette.hairline }}
    />
  ) : (
    <input
      value={text}
      aria-label={ariaLabel}
      placeholder={placeholder}
      maxLength={120}
      onChange={e => setText(e.target.value)}
      onBlur={onBlur}
      style={{ ...shared, borderColor: palette.hairline }}
    />
  )
}
